import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react'
import { TermsOfServiceModal } from './modals/TermsOfServiceModal'

/**
 * LocalStorage key for storing terms acceptance
 * Using a versioned key allows us to re-prompt users when terms change
 */
const TERMS_ACCEPTED_KEY = 'ctopools_terms_accepted_v1'

/**
 * Interface for TermsContext value
 * Provides methods to check and update terms acceptance status
 */
interface TermsContextValue {
  /** Whether the user has accepted the current version of terms */
  hasAcceptedTerms: boolean
  /** Check if user should be prompted for terms (for wallet connection) */
  shouldPromptTerms: boolean
  /** Function to show the terms modal */
  promptTermsAcceptance: () => void
  /** Function to mark terms as accepted */
  acceptTerms: () => void
  /** Function to revoke terms acceptance (for testing/settings) */
  revokeTermsAcceptance: () => void
  /** Callback to be executed after terms are accepted (e.g., open wallet modal) */
  setOnTermsAccepted: (callback: (() => void) | null) => void
}

/**
 * Default context value
 * Used when context is accessed outside of provider
 */
const defaultContextValue: TermsContextValue = {
  hasAcceptedTerms: false,
  shouldPromptTerms: true,
  promptTermsAcceptance: () => {},
  acceptTerms: () => {},
  revokeTermsAcceptance: () => {},
  setOnTermsAccepted: () => {}
}

/**
 * Terms Context
 * Provides terms acceptance state and methods throughout the app
 */
const TermsContext = createContext<TermsContextValue>(defaultContextValue)

/**
 * Props for TermsProvider component
 */
interface TermsProviderProps {
  children: ReactNode
}

/**
 * TermsProvider Component
 * 
 * Wraps the application to provide terms acceptance state management.
 * This simplified version:
 * - Persists terms acceptance in localStorage
 * - Shows the terms modal when prompted
 * - Supports an onAccept callback for chaining actions (like opening wallet modal)
 * - Does NOT watch wallet connections or auto-disconnect
 * 
 * The new UX flow (using WalletConnectButton):
 * 1. User clicks "Select Wallet" button
 * 2. If TOS not accepted → TOS modal shows first
 * 3. User accepts TOS → callback fires to open wallet modal
 * 4. Wallet modal opens
 * 5. User connects once (no double connection!)
 */
export function TermsProvider({ children }: TermsProviderProps): JSX.Element {
  // State for tracking terms acceptance
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState<boolean>(false)
  // State for controlling modal visibility
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
  // Callback to execute after terms are accepted (e.g., open wallet modal)
  const [onTermsAccepted, setOnTermsAcceptedState] = useState<(() => void) | null>(null)
  // Flag to track if we've done initial check (prefixed with _ to indicate intentionally unused externally)
  const [_isInitialized, setIsInitialized] = useState<boolean>(false)

  /**
   * Load terms acceptance status from localStorage on mount
   * Checks if user has previously accepted the current version of terms
   */
  useEffect(() => {
    try {
      const stored = localStorage.getItem(TERMS_ACCEPTED_KEY)
      console.log('[TermsProvider] Checking localStorage for TOS acceptance:', stored)
      
      if (stored) {
        const data = JSON.parse(stored)
        // Check if stored acceptance is valid and not expired
        if (data.accepted && data.timestamp) {
          // Acceptance is valid (could add expiry check here if needed)
          console.log('[TermsProvider] TOS already accepted on:', data.timestamp)
          setHasAcceptedTerms(true)
        }
      } else {
        console.log('[TermsProvider] TOS not yet accepted - will show modal on wallet connect')
      }
    } catch (error) {
      console.error('Error loading terms acceptance status:', error)
    }
    setIsInitialized(true)
  }, [])

  /**
   * Calculate whether we should prompt for terms
   * Used by components to determine if they should show a terms prompt
   */
  const shouldPromptTerms = !hasAcceptedTerms

  /**
   * Show the terms modal
   * Called when user attempts an action that requires terms acceptance
   */
  const promptTermsAcceptance = useCallback(() => {
    setIsModalOpen(true)
  }, [])

  /**
   * Accept terms and persist to localStorage
   * Records the acceptance timestamp for audit purposes
   * Also executes the onTermsAccepted callback if set (e.g., to open wallet modal)
   */
  const acceptTerms = useCallback(() => {
    try {
      const data = {
        accepted: true,
        timestamp: new Date().toISOString(),
        version: 'v1'
      }
      localStorage.setItem(TERMS_ACCEPTED_KEY, JSON.stringify(data))
      setHasAcceptedTerms(true)
      setIsModalOpen(false)

      // Execute callback if set (e.g., open wallet modal after TOS acceptance)
      if (onTermsAccepted) {
        // Small delay to ensure modal closes before next action
        setTimeout(() => {
          onTermsAccepted()
          setOnTermsAcceptedState(null)
        }, 100)
      }
    } catch (error) {
      console.error('Error saving terms acceptance:', error)
    }
  }, [onTermsAccepted])

  /**
   * Revoke terms acceptance
   * Removes acceptance from localStorage (for testing or user settings)
   */
  const revokeTermsAcceptance = useCallback(() => {
    try {
      localStorage.removeItem(TERMS_ACCEPTED_KEY)
      setHasAcceptedTerms(false)
    } catch (error) {
      console.error('Error revoking terms acceptance:', error)
    }
  }, [])

  /**
   * Handle terms decline
   * Closes modal without accepting and clears any pending callback
   */
  const handleDecline = useCallback(() => {
    setIsModalOpen(false)
    setOnTermsAcceptedState(null)
  }, [])

  /**
   * Set callback for after terms acceptance
   * Allows setting a function to run after user accepts terms
   * Typically used to open the wallet modal after TOS acceptance
   */
  const setOnTermsAccepted = useCallback((callback: (() => void) | null) => {
    setOnTermsAcceptedState(() => callback)
  }, [])

  const contextValue: TermsContextValue = {
    hasAcceptedTerms,
    shouldPromptTerms,
    promptTermsAcceptance,
    acceptTerms,
    revokeTermsAcceptance,
    setOnTermsAccepted
  }

  return (
    <TermsContext.Provider value={contextValue}>
      {children}
      <TermsOfServiceModal
        isOpen={isModalOpen}
        onAccept={acceptTerms}
        onDecline={handleDecline}
      />
    </TermsContext.Provider>
  )
}

/**
 * useTerms Hook
 * 
 * Custom hook for accessing terms acceptance state and methods.
 * Must be used within a TermsProvider.
 * 
 * @returns TermsContextValue with acceptance state and control methods
 * @throws Error if used outside of TermsProvider
 * 
 * @example
 * ```tsx
 * const { hasAcceptedTerms, promptTermsAcceptance, setOnTermsAccepted } = useTerms()
 * 
 * const handleConnect = () => {
 *   if (!hasAcceptedTerms) {
 *     setOnTermsAccepted(() => openWalletModal)
 *     promptTermsAcceptance()
 *     return
 *   }
 *   openWalletModal()
 * }
 * ```
 */
export function useTerms(): TermsContextValue {
  const context = useContext(TermsContext)
  if (!context) {
    throw new Error('useTerms must be used within a TermsProvider')
  }
  return context
}

export default TermsProvider
